import React from 'react';
import { set } from 'lodash-es';
import { compareStrings } from 'utils';
import { Cell as CellComponent, HeaderCell } from '../table-cells';

// An accessor is either a string key/path or a v8 accessor function.
type Accessor = string | ((row: unknown) => unknown);

type SortMethod = (a: unknown, b: unknown) => number;

type HeaderFn = (data?: unknown, props?: unknown) => React.ReactNode;

interface CellRenderData {
  value?: React.ReactNode;
  [key: string]: unknown;
}

type CellFn = (data: CellRenderData, props?: Record<string, unknown>) => React.ReactNode;

// Row `original` objects are domain-specific; only `id` is read here.
interface RowOriginal {
  id?: string | number;
  [key: string]: unknown;
}

// The app's column descriptor. Extra descriptor keys pass through via `rest`.
interface ColumnDescriptor {
  path?: string;
  Header?: HeaderFn;
  HeaderContainer?: React.ElementType;
  headerProps?: (data: Record<string, unknown>) => Record<string, unknown>;
  Cell?: CellFn;
  CellContainer?: React.ElementType;
  cellProps?: (data: Record<string, unknown>) => Record<string, unknown>;
  alignLeft?: boolean;
  sortable?: boolean;
  sortMethod?: SortMethod;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  resizable?: boolean;
  accessor?: Accessor;
  id?: string;
  [key: string]: unknown;
}

// v8 cell context (minimal shape of the fields accessed here).
interface CellRenderCtx {
  getValue: () => React.ReactNode;
  row: { original: RowOriginal };
  [key: string]: unknown;
}

// v8 header context. `table` is present whenever v8 renders the header; the rest
// passes through untouched.
interface HeaderRowModel {
  rows: { original: RowOriginal }[];
}
interface HeaderRenderCtx extends Record<string, unknown> {
  table?: {
    getRowModel: () => HeaderRowModel;
    /** Present once getPaginationRowModel is installed, which FjSortTable does unless manual. */
    getPrePaginationRowModel?: () => HeaderRowModel;
  };
}

/** Rows in the shape a v6-style Header render prop expects: `{ _original }` per row. */
const asHeaderRows = (rows: { original: RowOriginal }[] = []): { _original: RowOriginal }[] =>
  rows.map(({ original }) => ({ _original: original }));

/**
 * `data`: the rows actually on screen. Empty when v8 has not supplied a table (nothing to
 * select).
 */
const rowsAsHeaderData = (ctx: HeaderRenderCtx): { _original: RowOriginal }[] =>
  asHeaderRows(ctx.table?.getRowModel?.().rows);

/**
 * `allData`: every row the table holds after filtering but before paging. The select-all
 * checkbox needs both to tell "select all" from "select all on this page" - `getRowModel()` is
 * post-pagination, so on its own it cannot see that further rows exist. Falls back to the page
 * when pagination is not installed (`manual`), where the two are the same thing anyway.
 */
const allRowsAsHeaderData = (ctx: HeaderRenderCtx): { _original: RowOriginal }[] =>
  asHeaderRows(ctx.table?.getPrePaginationRowModel?.().rows ?? ctx.table?.getRowModel?.().rows);

// v8 sorting row (minimal shape).
interface SortRow {
  getValue: (columnId: string) => unknown;
}

/**
 * Our key inside `columnDef.meta`, v8's own per-column escape hatch, where a column's
 * Header/Cell configuration is parked for the two renderer components below to read back at
 * render time. See `renderSpec` for why the configuration travels as data rather than inside a
 * closure.
 */
const FJ_RENDER_SPEC = 'fjRender';

interface EditPayload {
  path: Accessor;
  value: unknown;
  original: RowOriginal;
  updated: RowOriginal;
  delta: Record<string, unknown>;
  updatedEntry: Record<string, unknown>;
}

// The @tanstack/react-table v8 column def we produce. Built up dynamically, so
// the assignable fields are optional plus a pass-through index signature.
interface BuiltColumnDef {
  id: string;
  header?: unknown;
  cell?: (ctx: CellRenderCtx) => React.ReactNode;
  enableSorting?: boolean;
  sortingFn?: (rowA: SortRow, rowB: SortRow, columnId: string) => number;
  accessorFn?: (row: unknown) => unknown;
  accessorKey?: string;
  size?: number;
  minSize?: number;
  maxSize?: number;
  enableResizing?: boolean;
  [key: string]: unknown;
}

interface BuildColumnsArgs {
  columns?: ColumnDescriptor[];
  handleEdit: (payload: EditPayload) => unknown;
  sortState: unknown;
}

/**
 * What a column's Header needs at render time. `wrap` is the discriminant for the two branches
 * buildColumns has always had: a `path` column wraps its Header in HeaderContainer and hands it
 * the computed props as a second argument; a pass-through column renders the Header bare with
 * the context alone, and has none of the rest to give it.
 */
type HeaderSpec =
  | { wrap: false; Header: HeaderFn }
  | {
    wrap: true;
    Header: HeaderFn;
    HeaderContainer: React.ElementType;
    headerProps: (data: Record<string, unknown>) => Record<string, unknown>;
    accessor: Accessor;
    alignLeft: boolean;
    sortable: boolean;
    sortState: unknown;
    id: string;
  };

/** The Cell counterpart of HeaderSpec; `wrap` splits the same two branches. */
type CellSpec =
  | { wrap: false; Cell: CellFn }
  | {
    wrap: true;
    Cell: CellFn;
    CellContainer: React.ElementType;
    cellProps: (data: Record<string, unknown>) => Record<string, unknown>;
    accessor: Accessor;
    alignLeft: boolean;
    handleEdit: (payload: EditPayload) => unknown;
    id: string;
  };

interface RenderSpec {
  header?: HeaderSpec;
  cell?: CellSpec;
}

/**
 * The spec is reached through the render context rather than captured in a closure - that is the
 * whole point of it. `flexRender` turns whatever sits in `columnDef.header`/`columnDef.cell` into
 * the React element TYPE, because it calls `React.createElement` on any function it is given.
 * buildColumns runs again whenever FjSortTable is handed a new `columns` array, which the
 * cloud-admin tables do on every render, so emitting a fresh arrow function per build gave every
 * rebuild a brand-new element type. React cannot reconcile two different types in one position:
 * it unmounted the old header and mounted a new one, and any local state inside went with it -
 * the select-all scope modal closed itself mid-question (FJCLOUD-4494).
 *
 * So `header` and `cell` are the two fixed components below, and everything that varies per
 * column travels as data on the column def. A rebuild is then an ordinary prop change.
 */
const renderSpec = (ctx: Record<string, unknown>): RenderSpec => {
  const column = ctx.column as { columnDef?: { meta?: Record<string, unknown> } } | undefined;
  return (column?.columnDef?.meta?.[FJ_RENDER_SPEC] as RenderSpec | undefined) || {};
};

/**
 * Parks a column's render spec in `columnDef.meta`, merged over whatever meta the descriptor
 * already carried so a consumer's own meta survives alongside it.
 */
const withRenderSpec = (existingMeta: unknown, spec: RenderSpec): Record<string, unknown> => ({
  ...(existingMeta as Record<string, unknown> | undefined),
  [FJ_RENDER_SPEC]: spec,
});

/** The single element type behind every column header. */
const FjHeader = (ctx: HeaderRenderCtx): React.ReactNode => {
  const spec = renderSpec(ctx).header;
  if (!spec) return null;

  // v6 handed the Header render prop the table's rows as `data`, each wrapped as `{ _original }`;
  // v8's header context carries only { table, header, column }. The select-all checkboxes still
  // read `data`, so rebuild it here rather than teach every consumer the v8 shape - adapting v6
  // descriptors to v8 is what this file is for.
  const rows = { data: rowsAsHeaderData(ctx), allData: allRowsAsHeaderData(ctx) };

  if (!spec.wrap) return spec.Header({ ...ctx, ...rows });

  const { Header, HeaderContainer, headerProps, accessor, alignLeft, sortable, sortState, id } =
    spec;
  const headerData = { ...ctx, accessor, ...rows };
  const props = {
    sortState,
    alignLeft,
    sortable,
    dataTestId: `${id}-column-header`,
    ...headerProps(headerData),
  };
  return (
    <HeaderContainer columnName={id} {...props}>
      {Header(headerData, props)}
    </HeaderContainer>
  );
};

/** The single element type behind every column cell. */
const FjCell = (ctx: CellRenderCtx): React.ReactNode => {
  const spec = renderSpec(ctx).cell;
  if (!spec) return null;
  const { original } = ctx.row;
  const value = ctx.getValue();

  if (!spec.wrap) return spec.Cell({ ...ctx, value, original });

  const { Cell, CellContainer, cellProps, accessor, alignLeft, handleEdit, id } = spec;
  const onChange = (newValue: unknown) => {
    const updated = set({ ...original }, accessor as Parameters<typeof set>[1], newValue);
    const delta = set({}, accessor as Parameters<typeof set>[1], newValue);
    const baseKey = Object.keys(delta)[0];
    const updatedEntry = { [baseKey]: updated[baseKey] };
    return handleEdit({
      path: accessor,
      value: newValue,
      original,
      updated,
      delta,
      updatedEntry,
    });
  };
  const cellData = { ...ctx, value, path: accessor, original, onChange };
  const props = {
    alignLeft,
    dataTestId: `${id}__${original.id}`,
    ...cellProps(cellData),
  };
  // NOTE: CellContainer receives no spread props here — this preserves the
  // long-standing behavior of the original builder (it spread `cellProps`,
  // the function, which contributed no props). `props` still reaches the
  // consumer's Cell as its second argument.
  return <CellContainer>{Cell(cellData, props)}</CellContainer>;
};

// Translates the app's column descriptors into @tanstack/react-table v8 column
// defs. The descriptor shape (path/accessor/Header/Cell/sortMethod/width/…) is
// preserved so the 8 consumer screens need no changes — the v8 row/cell context
// is mapped back into the { value, original, onChange } shape their Header/Cell
// functions were written against under react-table-6.
export const buildColumns = ({
  columns = [],
  handleEdit,
  sortState,
}: BuildColumnsArgs): BuiltColumnDef[] =>
  columns.map((columnData, columnIndex) => {
    const {
      path,
      Header = () => null,
      HeaderContainer = HeaderCell,
      headerProps = () => ({}),
      Cell = ({ value }) => value,
      CellContainer = CellComponent,
      cellProps = () => ({}),
      alignLeft = false,
      sortable = true,
      // compareStrings narrows to (string, string); widen to SortMethod so it
      // accepts the `unknown` values `getValue` returns (runtime unchanged).
      sortMethod = compareStrings as SortMethod,
      width,
      minWidth,
      maxWidth,
      // react-table-6 defaulted every column to resizable; only an explicit
      // `resizable: false` opted out. Preserve that default here.
      resizable = true,
      accessor: accessorOverride,
      id: idOverride,
      ...rest
    } = columnData;

    // v6 width/minWidth/maxWidth -> v8 size/minSize/maxSize.
    const sizing = {
      ...(width != null && { size: width }),
      ...(minWidth != null && { minSize: minWidth }),
      ...(maxWidth != null && { maxSize: maxWidth }),
      enableResizing: resizable,
    };

    const applyAccessor = (def: BuiltColumnDef, accessor: Accessor | undefined): BuiltColumnDef => {
      if (typeof accessor === 'function') {
        def.accessorFn = accessor;
      } else if (accessor != null) {
        def.accessorKey = accessor;
      }
      return def;
    };

    // Thin pass-through columns (no `path`): Header/Cell used as given, no
    // sort-indicator wrapping and no editable onChange wiring — matching the
    // former `if (!path) return { Header, Cell, sortable, ...rest }` branch.
    if (!path) {
      const id =
        idOverride ||
        (typeof accessorOverride === 'string' ? accessorOverride : `col-${columnIndex}`);
      const def: BuiltColumnDef = {
        id,
        // v8 renders a string as-is, or calls the function with its own context. Where it is
        // a function, FjHeader stands in and hands it `data` too: this is the branch the
        // select-all checkbox columns take (they carry no `path`), and they read the row list
        // from it.
        header: typeof Header === 'function' ? FjHeader : Header,
        cell: FjCell,
        enableSorting: sortable,
        ...sizing,
        ...rest,
        meta: withRenderSpec(rest.meta, {
          ...(typeof Header === 'function' && { header: { wrap: false, Header } }),
          cell: { wrap: false, Cell },
        }),
      };
      applyAccessor(def, accessorOverride);
      if (def.accessorKey || def.accessorFn) {
        def.sortingFn = (rowA: SortRow, rowB: SortRow, columnId: string) =>
          sortMethod(rowA.getValue(columnId), rowB.getValue(columnId));
      } else {
        def.enableSorting = false;
      }
      return def;
    }

    const accessor: Accessor = accessorOverride || path;
    const id = idOverride || (typeof accessor === 'string' ? accessor : `col-${columnIndex}`);

    const def: BuiltColumnDef = {
      id,
      header: FjHeader,
      cell: FjCell,
      enableSorting: sortable,
      sortingFn: (rowA: SortRow, rowB: SortRow, columnId: string) =>
        sortMethod(rowA.getValue(columnId), rowB.getValue(columnId)),
      ...sizing,
      ...rest,
      meta: withRenderSpec(rest.meta, {
        header: {
          wrap: true,
          Header,
          HeaderContainer,
          headerProps,
          accessor,
          alignLeft,
          sortable,
          sortState,
          id,
        },
        cell: { wrap: true, Cell, CellContainer, cellProps, accessor, alignLeft, handleEdit, id },
      }),
    };
    return applyAccessor(def, accessor);
  });
